import React, { useState } from "react"
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Collapse,
  Badge,
} from "reactstrap"
import moment from "moment"

const CPSStockManagement = ({ entry, myUser, statusIndex, reload }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [newOrder, setNewOrder] = useState({
    quantity: "",
    deliveryDate: "",
  })
  const [stockUpdate, setStockUpdate] = useState({
    rawStock: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Determine if this step is active based on entry status
  const isActive = entry?.status === "Stock Management"
  const isCompleted = ["Completed"].includes(entry?.status)

  // Demo stock levels
  const demoStock = {
    rawStock: 850,
    minLevel: entry?.safetyStockMin || 100,
    maxLevel: entry?.safetyStockMax || 500,
    dateStarted: "2025-01-20",
    dateUpdated: "2025-01-25",
  }

  // Simple low-stock indicator based on demo stock and min level
  const isLowStock = demoStock.rawStock < demoStock.minLevel

  const handleNewOrder = async e => {
    e.preventDefault()
    if (!newOrder.quantity || !newOrder.deliveryDate) return

    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
      setNewOrder({ quantity: "", deliveryDate: "" })
      alert("New order request submitted!")
    }, 1500)
  }

  const handleStockUpdate = async e => {
    e.preventDefault()
    if (!stockUpdate.rawStock) return

    setIsSubmitting(true)

    setTimeout(() => {
      setIsSubmitting(false)
      setStockUpdate({ rawStock: "" })
      setIsOpen(false)
      alert("Stock levels updated!")
    }, 1500)
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isActive ? moment(demoStock.dateUpdated).format("DD.MM.YYYY") : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Stock Management{" "}
            {isActive
              ? ` - annual quantity management with automatic stock monitoring`
              : isCompleted
              ? ` - stock management completed`
              : ""}
          </div>
        </div>
      </div>

      {isActive && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Current Stock Levels:</small>
            {myUser?.userType === "Admin" && (
              <Button color="info" size="sm" onClick={() => setIsOpen(!isOpen)}>
                Update Stock
              </Button>
            )}
          </div>
          <div className="mb-2">
            <small className="text-muted">
              <strong>Raw Material Stock:</strong> {demoStock.rawStock} pcs{" "}
              <br />
              <strong>Available Raw Material Stock:</strong>{" "}
              {demoStock.rawStock - 350} pcs <br />
              <strong>Safety Range:</strong> {demoStock.minLevel} -{" "}
              {demoStock.maxLevel} pcs
            </small>
          </div>

          <Collapse isOpen={isOpen}>
            <div className="mt-3 p-3 border rounded">
              <h6 className="mb-3">Update Stock Levels</h6>
              <Form onSubmit={handleStockUpdate}>
                <FormGroup className="mb-2">
                  <Label>Raw Stock</Label>
                  <Input
                    type="number"
                    value={stockUpdate.rawStock}
                    onChange={e =>
                      setStockUpdate({
                        ...stockUpdate,
                        rawStock: e.target.value,
                      })
                    }
                    placeholder="Enter raw stock quantity"
                    disabled={isSubmitting}
                    min="0"
                  />
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    color="info"
                    size="sm"
                    disabled={!stockUpdate.rawStock || isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Stock"}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </Collapse>

          {/* Customer new order section */}
          {myUser?.userType !== "Admin" && (
            <div className="mt-3 pt-3 border-top">
              <h6 className="mb-2">Request New Batch</h6>
              <Form onSubmit={handleNewOrder}>
                <FormGroup className="mb-2">
                  <Input
                    type="number"
                    value={newOrder.quantity}
                    onChange={e =>
                      setNewOrder({ ...newOrder, quantity: e.target.value })
                    }
                    placeholder="Quantity needed"
                    disabled={isSubmitting}
                    min="1"
                    size="sm"
                  />
                </FormGroup>
                <FormGroup className="mb-2">
                  <Input
                    type="date"
                    value={newOrder.deliveryDate}
                    onChange={e =>
                      setNewOrder({ ...newOrder, deliveryDate: e.target.value })
                    }
                    disabled={isSubmitting}
                    size="sm"
                  />
                </FormGroup>
                <Button
                  type="submit"
                  color="primary"
                  size="sm"
                  disabled={
                    !newOrder.quantity || !newOrder.deliveryDate || isSubmitting
                  }
                >
                  {isSubmitting ? "Requesting..." : "Request New Batch"}
                </Button>
              </Form>
            </div>
          )}
        </div>
      )}

      {isCompleted && (
        <div className="mt-3">
          <small className="text-muted">
            <strong>Raw Materials:</strong> {demoStock.rawStock} pcs <br />
            <strong>Finished Products:</strong> {demoStock.finishedStock} pcs{" "}
            <br />
            <strong>Status:</strong>{" "}
            <Badge color={isLowStock ? "danger" : "success"} size="sm">
              {isLowStock ? "Low Stock" : "OK"}
            </Badge>
          </small>
        </div>
      )}

      <p className="mb-0 mt-2">
        <i className="bx bx-time-five font-size-13 align-middle me-1"></i>
        <span className="font-size-13">
          {isActive ? "Stock monitoring active" : "Not started"}
        </span>
      </p>
    </li>
  )
}

export default CPSStockManagement
